import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import ClientGstTab from './ClientGstTab';
import {
  Users, FileCheck, FileText, Loader2, Download, LayoutDashboard,
  ChevronRight, Building2, Upload, FolderLock, Archive, ShoppingCart,
  Clock, CheckCircle, RefreshCw, Receipt, LifeBuoy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getFileUrl = (url) => (!url ? '#' : url.startsWith('http') ? url : `${API_URL}${url}`);

export const CompanyFilter = ({ companies, value, onChange, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="h-9 px-3 text-sm bg-[#0f1611] border border-emerald-900/40 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
  >
    <option value="">All Companies</option>
    {companies.map((c) => (
      <option key={c.id} value={c.id}>{c.company_name}</option>
    ))}
  </select>
);

const SectionShell = ({ title, icon: Icon, companies, filter, setFilter, children, testId, action }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
      <h2 className="text-2xl font-serif text-white flex items-center gap-2.5">
        <Icon className="w-6 h-6 text-emerald-400" /> {title}
      </h2>
      <div className="flex items-center gap-2">
        {action}
        {companies && <CompanyFilter companies={companies} value={filter} onChange={setFilter} testId={`${testId}-company-filter`} />}
      </div>
    </div>
    {children}
  </div>
);

const Empty = ({ icon: Icon, text }) => (
  <div className="text-center py-14 bg-[#0f1611] rounded-xl border border-emerald-900/30">
    <Icon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
    <p className="text-slate-400 text-sm">{text}</p>
  </div>
);

// ==================== Dashboard Overview ====================
export const DashboardOverview = ({ stats, companies, orders, onSelectCompany }) => {
  const activeOrders = orders.filter((o) => o.status !== 'completed' && o.status !== 'cancelled').length;
  const completedOrders = orders.filter((o) => o.status === 'completed').length;
  const activeRenewals = orders.filter((o) => o.frequency_type && o.frequency_type !== 'one_time' && o.status !== 'completed' && o.status !== 'cancelled').length;

  const statCards = [
    { label: 'Companies', value: companies.length, icon: Building2 },
    { label: 'In Progress', value: activeOrders, icon: Clock },
    { label: 'Completed', value: completedOrders, icon: CheckCircle },
    { label: 'Active Renewals', value: activeRenewals, icon: RefreshCw },
  ];

  return (
    <div data-testid="portal-dashboard-overview">
      <h2 className="text-2xl font-serif text-white mb-1">Dashboard</h2>
      <p className="text-sm text-slate-400 mb-6">A quick view of your compliance activity</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((s) => (
          <div key={s.label} className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <s.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-serif text-white">{s.value}</p>
                <p className="text-xs text-slate-400">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5" data-testid="dashboard-companies">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-serif text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-400" /> My Companies
          </h3>
        </div>
        {companies.length === 0 ? <Empty icon={Building2} text="No companies yet" /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {companies.map((c) => {
              const cOrders = orders.filter((o) => o.company_id === c.id);
              const cActive = cOrders.filter((o) => o.status !== 'completed' && o.status !== 'cancelled').length;
              return (
                <button key={c.id} onClick={() => onSelectCompany(c.id)}
                  className="text-left p-4 bg-[#080b09] border border-emerald-900/20 rounded-lg hover:border-emerald-500/40 transition-colors flex items-center justify-between"
                  data-testid={`dashboard-company-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{c.company_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {c.entity_type?.replace(/_/g, ' ')} · {cOrders.length} order{cOrders.length !== 1 ? 's' : ''}
                      {cActive > 0 && <span className="text-amber-400"> · {cActive} active</span>}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Companies List ====================
export const CompaniesList = ({ companies, orders, onSelect, onAddCompany }) => (
  <div data-testid="portal-companies-list">
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-serif text-white">Companies</h2>
        <p className="text-sm text-slate-400 mt-0.5">Manage all your registered companies</p>
      </div>
      <button onClick={onAddCompany}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-black font-medium rounded-lg transition-colors"
        data-testid="add-company-btn">
        + Add Company
      </button>
    </div>

    {companies.length === 0 ? (
      <Empty icon={Building2} text="No companies yet — click Add Company to get started." />
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {companies.map((c) => {
          const cOrders = orders.filter((o) => o.company_id === c.id);
          const cActive = cOrders.filter((o) => o.status !== 'completed' && o.status !== 'cancelled').length;
          return (
            <button key={c.id} onClick={() => onSelect(c.id)}
              className="text-left p-5 bg-[#0f1611] border border-emerald-900/30 rounded-xl hover:border-emerald-500/50 transition-all"
              data-testid={`company-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-medium text-white truncate">{c.company_name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{c.entity_type?.replace(/_/g, ' ')}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                      {cOrders.length} order{cOrders.length !== 1 ? 's' : ''}
                    </span>
                    {cActive > 0 && (
                      <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400">
                        {cActive} active
                      </span>
                    )}
                    {c.state && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-700/40 text-slate-300">{c.state}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0 mt-1" />
              </div>
            </button>
          );
        })}
      </div>
    )}
  </div>
);

// ==================== Orders Section ====================
const FREQUENCY_TAB_META = [
  { key: 'all', label: 'All Orders', match: () => true },
  { key: 'one_time', label: 'One-Time', match: (ft) => !ft || ft === 'one_time' },
  { key: 'recurring', label: 'Recurring', match: (ft) => ft && ft !== 'one_time' },
];

const FREQUENCY_BADGE = {
  one_time: { label: 'One-Time', cls: 'bg-slate-500/15 text-slate-300 border border-slate-500/30' },
  monthly: { label: 'Monthly', cls: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' },
  quarterly: { label: 'Quarterly', cls: 'bg-blue-500/15 text-blue-400 border border-blue-500/30' },
  yearly_auto_renewal: { label: 'Yearly (Auto-Renew)', cls: 'bg-purple-500/15 text-purple-400 border border-purple-500/30' },
};

const GstCompanyView = ({ companyId }) => {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    if (!companyId) { setRegs([]); return; }
    setLoading(true);
    api.get(`/client/companies/${companyId}/gst`)
      .then((r) => setRegs(r.data?.registrations || []))
      .catch(() => { setRegs([]); toast.error('Failed to load GST data'); })
      .finally(() => setLoading(false));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto my-12" />;
  if (regs.length === 0) return <Empty icon={FileText} text="No GST registrations for this company yet." />;
  return <ClientGstTab companyId={companyId} registrations={regs} onReload={load} />;
};

export const OrdersSection = ({ orders, companies, getOrderStatusDisplay, formatCurrency }) => {
  const [filter, setFilter] = useState('');
  const [view, setView] = useState('orders');
  const [freqTab, setFreqTab] = useState('all');
  const tab = FREQUENCY_TAB_META.find((t) => t.key === freqTab) || FREQUENCY_TAB_META[0];
  const list = orders.filter((o) => (!filter || o.company_id === filter) && tab.match(o.product?.frequency_type));
  const counts = FREQUENCY_TAB_META.reduce((acc, t) => {
    acc[t.key] = orders.filter((o) => (!filter || o.company_id === filter) && t.match(o.product?.frequency_type)).length;
    return acc;
  }, {});
  return (
    <div data-testid="portal-orders">
      {/* Header: title + view switcher + company dropdown */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-2xl font-serif text-white flex items-center gap-2.5">
            <ShoppingCart className="w-6 h-6 text-emerald-400" /> Orders
          </h2>
          <div className="inline-flex p-1 bg-[#0f1611] border border-emerald-900/30 rounded-lg" data-testid="orders-view-switcher">
            {[['orders', 'Orders'], ['gst', 'GST Filings']].map(([k, lbl]) => (
              <button key={k} onClick={() => { setView(k); if (k === 'gst' && !filter && companies[0]) setFilter(companies[0].id); }} data-testid={`orders-view-${k}`}
                className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-all ${
                  view === k ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/40' : 'text-slate-400 hover:text-white border border-transparent'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <CompanyFilter companies={companies} value={filter} onChange={setFilter} testId="portal-gst-company-filter" />
      </div>

      {view === 'gst' ? (
        <div data-testid="portal-gst">
          <p className="text-sm text-slate-400 mb-5 -mt-2">Track your GST filings, upload data and confirm returns — pick a company on the top right.</p>
          {filter ? <GstCompanyView companyId={filter} />
            : <Empty icon={FileText} text="Select a company on the top right to view its GST filings." />}
        </div>
      ) : (
        <>
          {/* Frequency Tabs */}
          <div className="grid grid-cols-3 gap-2 mb-5 p-1 bg-[#0f1611] border border-emerald-900/30 rounded-xl" data-testid="orders-frequency-tabs">
            {FREQUENCY_TAB_META.map((t) => (
              <button
                key={t.key}
                onClick={() => setFreqTab(t.key)}
                data-testid={`orders-freq-${t.key}`}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  freqTab === t.key
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 shadow-sm shadow-emerald-500/10'
                    : 'text-slate-400 hover:text-white border border-transparent'
                }`}
              >
                {t.label}
                <span className="ml-2 text-xs opacity-70">({counts[t.key] || 0})</span>
              </button>
            ))}
          </div>

          {list.length === 0 ? <Empty icon={FileText} text={freqTab === 'recurring' ? 'No recurring orders yet.' : freqTab === 'one_time' ? 'No one-time orders yet.' : 'No orders found'} /> : (
            <div className="space-y-3">
              {list.map((order) => {
                const cfg = getOrderStatusDisplay(order);
                const StatusIcon = cfg.icon;
                const ft = order.product?.frequency_type || 'one_time';
                const freqMeta = FREQUENCY_BADGE[ft] || FREQUENCY_BADGE.one_time;
                return (
                  <Link key={order.id} to={`/orders/${order.id}`} data-testid={`portal-order-${order.id}`}
                    className="flex items-center justify-between p-4 bg-[#0f1611] rounded-xl border border-emerald-900/30 hover:border-emerald-500/50 transition-colors">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-white truncate">{order.display_name || order.product?.name || `Order #${order.id.slice(0, 8)}`}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded ${freqMeta.cls}`}>{freqMeta.label}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{order.company?.company_name} · {formatCurrency(order.amount || order.product?.total_price || order.total_amount || 0)}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${cfg.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" /> {cfg.label}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== Documents / DocVault / DataRoom ====================
const useDocuments = () => {
  const [data, setData] = useState({ acknowledgements: [], uploads: [], companies: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/my/documents').then((r) => setData(r.data)).catch(() => toast.error('Failed to load documents')).finally(() => setLoading(false));
  }, []);
  return { data, loading };
};

const DocRow = ({ doc, icon: Icon, tint }) => {
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    if (!doc.url || busy) return;
    setBusy(true);
    try {
      // Strip the '/api' prefix because the axios instance already has baseURL = `${API_URL}/api`
      const relative = doc.url.startsWith('http') ? doc.url : doc.url.replace(/^\/api/, '');
      const res = await api.get(relative, { responseType: 'blob' });
      const blob = res.data;
      const blobUrl = URL.createObjectURL(blob);
      // Trigger download with the original filename
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.name || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Also open in a new tab for inline preview
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      // Clean up after a short delay so the tab has time to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired — please log in again to download.');
      } else if (status === 404) {
        toast.error('File not found on server.');
      } else {
        toast.error('Failed to download document.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-[#0f1611] rounded-lg border border-emerald-900/30" data-testid="document-row">
      <div className="flex items-center gap-3 min-w-0">
        <Icon className={`w-5 h-5 flex-shrink-0 ${tint}`} />
        <div className="min-w-0">
          <p className="text-sm text-white truncate">{doc.name}</p>
          <p className="text-xs text-slate-500 truncate">{doc.company_name} · {doc.order_name}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy || !doc.url}
        className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 ml-3 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="doc-download-btn"
        title={doc.url ? 'Download document' : 'File not available'}
      >
        {busy
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <Download className="w-4 h-4" />
        }
        <span className="hidden sm:inline">{busy ? 'Downloading…' : 'Download'}</span>
      </button>
    </div>
  );
};

export const DocVaultSection = () => {
  const { data, loading } = useDocuments();
  const [filter, setFilter] = useState('');
  const list = (data.acknowledgements || []).filter((d) => !filter || d.company_id === filter);
  return (
    <SectionShell title="DocVault" icon={FolderLock} companies={data.companies || []} filter={filter} setFilter={setFilter} testId="portal-docvault">
      <p className="text-sm text-slate-400 mb-5 -mt-3">Filing acknowledgements, government certificates and signed documents.</p>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto my-10" /> :
        list.length === 0 ? <Empty icon={FolderLock} text="Vault is empty — certificates appear here when filings are completed." /> : (
          <div className="space-y-2">{list.map((d) => <DocRow key={d.id} doc={d} icon={FileCheck} tint="text-emerald-400" />)}</div>
        )}
    </SectionShell>
  );
};

export const DataRoomSection = () => {
  const { data, loading } = useDocuments();
  const [filter, setFilter] = useState('');
  const list = (data.uploads || []).filter((d) => !filter || d.company_id === filter);
  return (
    <SectionShell title="Data Room" icon={Archive} companies={data.companies || []} filter={filter} setFilter={setFilter} testId="portal-dataroom">
      <p className="text-sm text-slate-400 mb-5 -mt-3">All raw documents you have uploaded across your orders.</p>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto my-10" /> :
        list.length === 0 ? <Empty icon={Archive} text="No uploaded documents yet." /> : (
          <div className="space-y-2">{list.map((d) => <DocRow key={d.id} doc={d} icon={FileText} tint="text-slate-400" />)}</div>
        )}
    </SectionShell>
  );
};


// ==================== Invoices Section ====================
export const InvoicesSection = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    api.get('/invoices').then((r) => setInvoices(r.data || []))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async (inv) => {
    setDownloading(inv.id);
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${inv.invoice_number.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download PDF');
    } finally { setDownloading(null); }
  };

  const payNow = async (inv) => {
    if (!inv.order_id) { toast.error('No order linked to this invoice'); return; }
    setPayingId(inv.id);
    try {
      const res = await api.post(`/orders/${inv.order_id}/payment-link`);
      const url = res.data?.short_url;
      if (res.data?.status === 'already_paid') {
        toast.info('This invoice is already paid');
      } else if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success('Opening Razorpay payment page…');
      } else {
        toast.error('Could not generate payment link');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start payment');
    } finally { setPayingId(null); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const inr = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  return (
    <div data-testid="portal-invoices">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-serif text-white flex items-center gap-2.5">
            <Receipt className="w-6 h-6 text-emerald-400" /> Invoices
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">Tax invoices generated automatically when payment is completed</p>
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto my-10" />
      ) : invoices.length === 0 ? (
        <div className="text-center py-14 bg-[#0f1611] rounded-xl border border-emerald-900/30">
          <Receipt className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No invoices yet — they appear here once payment is completed on an order.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-emerald-900/30 bg-[#0f1611]" data-testid="invoices-table">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-[#080b09]">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Invoice #</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Order / Service</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Company</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Amount</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Date</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-emerald-900/20 hover:bg-emerald-500/5 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-emerald-400">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-white">
                    {inv.line_items?.[0]?.description || '—'}
                    <div className="text-[11px] text-slate-500 mt-0.5 uppercase">
                      {inv.gst_type === 'cgst_sgst' ? 'CGST + SGST' : 'IGST'} · SAC {inv.line_items?.[0]?.sac_code || '998231'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{inv.client_name || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium text-white">{inr(inv.total_amount)}</td>
                  <td className="px-4 py-3 text-slate-300">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      inv.status === 'paid'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : inv.status === 'pending' || inv.status === 'sent'
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-slate-600/30 text-slate-400'
                    }`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                        <button onClick={() => payNow(inv)} disabled={payingId === inv.id}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
                          data-testid={`invoice-pay-${inv.id}`}>
                          {payingId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          Pay Now
                        </button>
                      )}
                      <button onClick={() => downloadInvoice(inv)} disabled={downloading === inv.id}
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                        data-testid={`invoice-download-${inv.id}`}>
                        {downloading === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== Nav ====================
export const PORTAL_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'companies', label: 'Companies', icon: Building2 },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'tickets', label: 'Communication', icon: LifeBuoy },
  { id: 'docvault', label: 'DocVault', icon: FolderLock },
  { id: 'dataroom', label: 'Data Room', icon: Archive },
];
